/**
 * 角色服务
 * 封装所有角色相关的 API 调用和业务逻辑
 */
import { apiClient } from "../../../config/netConfig";
import { message } from "antd";

// 接口定义
export interface Role {
  id: string;
  role_name: string;
  role_code: string;
  description?: string;
  isAssigned?: boolean; // 表示该角色是否已分配给当前管理员
}

export interface AssignRolesParams {
  adminId: string;
  roleIds: string[];
}

export interface ApiResponse<T = any> {
  success: boolean;
  data: T;
  message: string;
}

// 统一的 API 请求处理函数
const request = async <T>(url: string, options?: any): Promise<ApiResponse<T>> => {
  try {
    const response = await apiClient.request({ url, ...options });
    const responseData = response.data;

    if (response.status === 200) {
      // 如果响应已经包含标准格式
      if ("success" in responseData && "data" in responseData && "message" in responseData) {
        return responseData;
      }
      // 否则包装成标准格式
      return { success: true, data: responseData, message: "操作成功" };
    } else {
      // 处理 API 错误
      const errorMessage = responseData.message || `请求失败: ${response.status}`;
      message.error(errorMessage);
      return { success: false, data: responseData, message: errorMessage };
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "请求失败";
    message.error(errorMessage);
    throw error; // 重新抛出，让调用方处理
  }
};

export const RoleService = {
  /**
   * 获取管理员角色列表（包含分配状态）
   */
  getAdminRoles: async (adminId: string): Promise<ApiResponse<Role[]>> => {
    return request(`/admin/${adminId}/roles`);
  },

  /**
   * 为管理员分配角色
   */
  assignRolesToAdmin: async (params: AssignRolesParams): Promise<ApiResponse<any>> => {
    return request(`/admin/${params.adminId}/roles`, {
      method: "POST",
      data: { roleIds: params.roleIds },
    });
  },

  /**
   * 获取所有角色列表
   */
  getRoleList: async (): Promise<ApiResponse<Role[]>> => {
    return request("/role/list");
  },
};